import FormSection from "../../../common/components/functional/formSection/FormSection";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectTalentById,
  updateTalentLanguages,
} from "../../talents/talentsSlice";
import {
  Language,
  Talent,
  TalentLanguages,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import { useTranslation } from "react-i18next";
import { Backdrop, Grid } from "@mui/material";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import FormTable, {
  DataItem,
} from "../../../common/components/functional/formTable/FormTable";
import { ContentKey } from "../TalentDetails";
import { isFulfilled } from "@reduxjs/toolkit";
import isEmpty from "../../../common/helpers/isEmpty";
import { UpdateTalentLanguages } from "../../talents/types/commands";
import { AccessRightContext } from "../../user/AccessRightProvider";
import useLanguageSkills from "../../../hooks/useLanguageSkills";

type Props = {
  handleOpenEditModal: (content: ContentKey, id?: string) => void;
};

const TalentLanguageSkills = ({ handleOpenEditModal }: Props) => {
  const { t } = useTranslation();
  const { canEditTalentProfile } = useContext(AccessRightContext);
  const { talentId } = useParams();
  const dispatch = useAppDispatch();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const { getLanguageSkillsNameByCode } = useLanguageSkills();

  const [isLoading, setIsLoading] = useState(false);

  const languages: TalentLanguages | undefined = talent.languages;
  const values: Language[] = languages?.values || [];

  const formattedData: DataItem[] = values
    .map((language) => ({
      values: {
        language: getLanguageSkillsNameByCode(language.languageCode),
        level: t("languageSkill:" + language.skillLevel),
      },
      id: language.id,
      deletable: true,
    }))
    .sort((a, b) => a.values.language.localeCompare(b.values.language));

  const deleteLanguage = (id: string) => {
    const currentValues = talent.languages?.values || [];
    const currentVersion = talent.languages?.version || 0;

    if (talentId && !isEmpty(currentValues) && currentVersion) {
      setIsLoading(true);

      const newValues = currentValues.filter((value) => value.id !== id);

      const commandPayload: UpdateTalentLanguages = {
        talentId: talentId,
        version: currentVersion,
        languages: newValues,
      };

      dispatch(updateTalentLanguages(commandPayload)).then((action) => {
        setIsLoading(false);

        return isFulfilled(action);
      });
    } else {
      setIsLoading(false);
    }
  };

  const headers = [
    { text: t("languageSkill:language") },
    { text: t("languageSkill:level") },
  ];
  const addButtonLabel = t("languageSkill:add");
  const title = t("languageSkill:skill");

  return (
    <FormSection title={title} margin>
      <Grid container columnSpacing={2}>
        <Backdrop
          open={isLoading}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            zIndex: 1000,
          }}
        >
          <LoadingSpinner />
        </Backdrop>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        />
        <Grid item xs={9}>
          <FormTable
            data={formattedData}
            headers={headers}
            handleOpenEditModal={handleOpenEditModal}
            handleDelete={deleteLanguage}
            contentKey="languages"
            addButtonLabel={addButtonLabel}
            disableEditRow={
              !canEditTalentProfile || talentIsDeactivated(talent)
            }
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default TalentLanguageSkills;
